import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tooltip,
  InputBase,
  IconButton,
  LinearProgress,
  Switch,
  Skeleton,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import BoltIcon from '@mui/icons-material/Bolt';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import { TSources } from './utils/sources';
import { colors } from '@src/styles';
import { fadeInUpSX, staggerChildrenSX } from '@src/styles/animations';
import { FlowDiagramPanel } from './components/FlowDiagramPanel';

export type TAutomationsView = {
  isLoading: boolean;
  isLoadingEventHandlers: boolean;
  progress: number;
  eventHandlers: Record<string, unknown> | undefined;
  pathStack: string[];
  searchTerm: string;
  filteredItems: [string, unknown][];
  dataSource: string;
  port: number;
  eventHandlersEnabled: boolean;
  onEventHandlersToggle: (value: boolean) => void;
  onReloadEvents: () => void;
  onGoBack: () => void;
  onGoToLevel: (depth: number) => void;
  selectedFlowPath: string | null;
  onCloseFlow: () => void;
  onItemClick: (key: string, value: unknown) => void;
  onSearchChange: (value: string) => void;
  onSourceChange: (dataSource: TSources, port: number) => void;
};

type TEventInfo = { source: string; detailType: string };

const titleFontFamily = "'Inter Tight', 'Inter', sans-serif";
const monoFontFamily = "'JetBrains Mono', monospace";

const monoLabelSX = {
  fontFamily: monoFontFamily,
  fontSize: '10px',
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: colors.brand.textMuted,
} as const;

const tonePillSX = (color: string) =>
  ({
    display: 'inline-flex',
    alignItems: 'center',
    height: 20,
    paddingX: 1,
    flexShrink: 0,
    borderRadius: '999px',
    border: `1px solid ${color}55`,
    backgroundColor: `${color}14`,
    color,
    fontFamily: monoFontFamily,
    fontSize: '9.5px',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  }) as const;

const SOURCES: { id: TSources; label: string }[] = [
  { id: 'default', label: 'Padrão' },
  { id: 'localhost', label: 'Localhost' },
];

const LAYOUT_EASE = 'cubic-bezier(0.2, 0.7, 0.2, 1)';
const LAYOUT_DURATION_MS = 420;
const MODAL_WIDTH = 780;
const SPLIT_EXPLORER_WIDTH = 340;

const LEVEL_NOUNS: [string, string][] = [
  ['organização', 'organizações'],
  ['automação', 'automações'],
  ['fluxo', 'fluxos'],
];

const countChildren = (value: unknown): number =>
  value && typeof value === 'object' ? Object.keys(value as Record<string, unknown>).length : 0;

export const AutomationsView = ({
  isLoading,
  isLoadingEventHandlers,
  progress,
  eventHandlers,
  pathStack,
  searchTerm,
  filteredItems,
  dataSource,
  port,
  eventHandlersEnabled,
  onEventHandlersToggle,
  onReloadEvents,
  onGoBack,
  onGoToLevel,
  selectedFlowPath,
  onCloseFlow,
  onItemClick,
  onSearchChange,
  onSourceChange,
}: TAutomationsView) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const searchRef = useRef<HTMLInputElement>(null);
  const depth = pathStack.length;
  const [levelNoun, levelNounPlural] = LEVEL_NOUNS[Math.min(depth, LEVEL_NOUNS.length - 1)];
  const searchPlaceholder = depth === 0 ? 'Buscar organização…' : `Buscar em ${pathStack[depth - 1]}…`;
  const hasEventsLoaded = eventHandlersEnabled && !isLoadingEventHandlers && Boolean(eventHandlers);

  const openItem = (key: string, value: unknown) => {
    onItemClick(key, value);
    searchRef.current?.focus();
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && filteredItems.length > 0) {
      event.preventDefault();
      openItem(filteredItems[0][0], filteredItems[0][1]);
    } else if (event.key === 'Escape' && searchTerm) {
      event.preventDefault();
      onSearchChange('');
    } else if (event.key === 'Backspace' && !searchTerm && depth > 0) {
      event.preventDefault();
      onGoBack();
    }
  };

  const isSplit = Boolean(selectedFlowPath);

  const [renderedFlowPath, setRenderedFlowPath] = useState(selectedFlowPath);
  useEffect(() => {
    if (selectedFlowPath) {
      setRenderedFlowPath(selectedFlowPath);
      return undefined;
    }
    const timer = window.setTimeout(() => setRenderedFlowPath(null), LAYOUT_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [selectedFlowPath]);

  const explorer = (
    <Paper
      elevation={0}
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: isMobile && !isSplit ? MODAL_WIDTH : 'none',
        height: isMobile ? (isSplit ? '100%' : 'calc(100vh - 92px)') : '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: colors.brand.surface,
        backgroundImage: 'none',
        border: `1px solid ${colors.brand.line}`,
        borderRadius: '14px',
        boxShadow: '0 30px 80px rgba(0, 0, 0, 0.35)',
        fontFamily: titleFontFamily,
        ...fadeInUpSX(),
      }}
    >
      {isLoadingEventHandlers && (
        <LinearProgress
          variant="determinate"
          value={progress}
          aria-label="Carregando eventos"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            backgroundColor: 'transparent',
            '& .MuiLinearProgress-bar': { backgroundColor: colors.primary.yellow },
          }}
        />
      )}

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          gap: 2,
          padding: { xs: '16px 16px 12px', md: '20px 22px 14px' },
        }}
      >
        <Box sx={{ flex: '999 1 240px', minWidth: 0 }}>
          <Typography
            component="h1"
            sx={{
              fontFamily: titleFontFamily,
              fontSize: '24px',
              fontWeight: 500,
              letterSpacing: '-0.03em',
              lineHeight: 1.1,
              color: colors.primary.white,
            }}
          >
            Automations
          </Typography>
          {!isSplit && (
            <Typography sx={{ marginTop: '2px', fontSize: '12.5px', color: colors.brand.textSecondary }}>
              Navegue pelos fluxos de cada organização.
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Box
            role="radiogroup"
            aria-label="Fonte dos fluxos"
            sx={{
              display: 'inline-flex',
              padding: '3px',
              borderRadius: '10px',
              backgroundColor: colors.background.primary,
              border: `1px solid ${colors.brand.line}`,
            }}
          >
            {SOURCES.map((source) => {
              const isActive = dataSource === source.id;
              return (
                <Box
                  key={source.id}
                  component="button"
                  type="button"
                  role="radio"
                  aria-checked={isActive}
                  onClick={() => onSourceChange(source.id, port)}
                  sx={{
                    height: 28,
                    paddingX: 1.25,
                    border: 'none',
                    borderRadius: '7px',
                    cursor: 'pointer',
                    fontFamily: titleFontFamily,
                    fontSize: '12.5px',
                    fontWeight: isActive ? 600 : 500,
                    backgroundColor: isActive ? colors.brand.surface : 'transparent',
                    boxShadow: isActive ? `inset 0 0 0 1px ${colors.brand.line}` : 'none',
                    color: isActive ? colors.primary.white : colors.brand.textSecondary,
                    '&:hover': { color: colors.primary.white },
                  }}
                >
                  {source.label}
                </Box>
              );
            })}
          </Box>

          {dataSource === 'localhost' && (
            <Box
              component="label"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                height: 36,
                paddingX: 1.25,
                borderRadius: '10px',
                border: `1px solid ${colors.brand.line}`,
                backgroundColor: colors.background.primary,
              }}
            >
              <Typography sx={monoLabelSX}>Porta</Typography>
              <InputBase
                type="number"
                value={port}
                onChange={(event) => onSourceChange(dataSource as TSources, Number(event.target.value))}
                inputProps={{ 'aria-label': 'Porta do localhost', min: 1, max: 65535 }}
                sx={{
                  width: 58,
                  color: colors.primary.white,
                  fontFamily: monoFontFamily,
                  fontSize: '13px',
                }}
              />
            </Box>
          )}

          <Tooltip
            title={
              eventHandlersEnabled
                ? 'Eventos carregados — ficam salvos enquanto esta aba estiver aberta'
                : 'Busca os event handlers de todas as organizações ativas (pode levar alguns segundos)'
            }
            arrow
          >
            <Box
              component="label"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                height: 36,
                paddingLeft: 1.25,
                borderRadius: '10px',
                border: `1px solid ${eventHandlersEnabled ? 'rgba(255, 160, 21, 0.4)' : colors.brand.line}`,
                backgroundColor: colors.background.primary,
                cursor: eventHandlersEnabled ? 'default' : 'pointer',
              }}
            >
              <Typography sx={{ fontSize: '12.5px', color: colors.brand.textStrong, whiteSpace: 'nowrap' }}>
                {isLoadingEventHandlers ? `Eventos ${Math.round(progress)}%` : 'Eventos'}
              </Typography>
              <Switch
                size="small"
                checked={eventHandlersEnabled}
                onChange={(event) => onEventHandlersToggle(event.target.checked)}
                disabled={eventHandlersEnabled}
                inputProps={{ 'aria-label': 'Carregar eventos' }}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: colors.primary.yellow },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: colors.primary.yellow,
                    opacity: 0.45,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked.Mui-disabled': { color: colors.primary.yellow },
                  '& .MuiSwitch-track': { backgroundColor: colors.brand.textMuted },
                }}
              />
            </Box>
          </Tooltip>

          {hasEventsLoaded && (
            <Tooltip title="Recarregar eventos" arrow>
              <IconButton
                onClick={onReloadEvents}
                aria-label="Recarregar eventos"
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '10px',
                  border: `1px solid ${colors.brand.line}`,
                  color: colors.brand.textSecondary,
                  '&:hover': { borderColor: colors.primary.yellow, color: colors.primary.white },
                }}
              >
                <RefreshIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Box sx={{ paddingX: { xs: 2, md: '22px' }, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Box
          component="nav"
          aria-label="Caminho"
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 0.5,
            minHeight: 22,
            fontFamily: monoFontFamily,
            fontSize: '11.5px',
          }}
        >
          {['organizações', ...pathStack].map((segment, index) => {
            const isCurrent = index === depth;
            return (
              <Box key={`${segment}-${index}`} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                {index > 0 && (
                  <Box component="span" aria-hidden sx={{ color: colors.brand.textMuted }}>
                    /
                  </Box>
                )}
                {isCurrent ? (
                  <Box component="span" aria-current="page" sx={{ color: colors.primary.yellow }}>
                    {segment}
                  </Box>
                ) : (
                  <Box
                    component="button"
                    type="button"
                    onClick={() => onGoToLevel(index)}
                    sx={{
                      padding: 0,
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      fontFamily: monoFontFamily,
                      fontSize: '11.5px',
                      color: colors.brand.textSecondary,
                      '&:hover': { color: colors.primary.white, textDecoration: 'underline' },
                    }}
                  >
                    {segment}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={depth > 0 ? 'Voltar um nível (Backspace)' : ''} arrow>
            <span>
              <IconButton
                onClick={onGoBack}
                disabled={depth === 0}
                aria-label="Voltar um nível"
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '10px',
                  border: `1px solid ${colors.brand.line}`,
                  color: colors.brand.textSecondary,
                  '&:hover': { borderColor: colors.primary.yellow, color: colors.primary.white },
                  '&.Mui-disabled': { color: colors.brand.textMuted, opacity: 0.45 },
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              height: 44,
              paddingX: 1.5,
              borderRadius: '10px',
              border: `1px solid ${colors.brand.line}`,
              backgroundColor: colors.background.primary,
              transition: 'border-color 0.15s ease',
              '&:focus-within': { borderColor: colors.primary.yellow },
            }}
          >
            <SearchIcon sx={{ fontSize: 18, color: colors.brand.textMuted }} />
            <InputBase
              inputRef={searchRef}
              autoFocus
              value={searchTerm}
              onChange={(event) => onSearchChange(event.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder={searchPlaceholder}
              inputProps={{ 'aria-label': searchPlaceholder }}
              sx={{
                flex: 1,
                color: colors.primary.white,
                fontFamily: titleFontFamily,
                fontSize: '14px',
                '& input::placeholder': { color: colors.brand.textMuted, opacity: 1 },
              }}
            />
            {searchTerm && (
              <Box component="kbd" sx={{ ...monoLabelSX, fontSize: '9.5px', letterSpacing: '0.08em' }}>
                Esc
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Box
        role="listbox"
        aria-label={levelNounPlural}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          margin: { xs: '10px 8px 0', md: '12px 14px 0' },
          paddingBottom: 1,
        }}
      >
        {isLoading ? (
          Array.from({ length: 10 }, (_, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, height: 42, paddingX: 1 }}>
              <Skeleton variant="rounded" width={18} height={18} sx={{ backgroundColor: 'rgba(255,255,255,0.06)' }} />
              <Skeleton
                variant="text"
                width={`${30 + ((index * 17) % 40)}%`}
                height={18}
                sx={{ backgroundColor: 'rgba(255,255,255,0.06)' }}
              />
            </Box>
          ))
        ) : filteredItems.length === 0 ? (
          <Box sx={{ padding: '40px 16px', textAlign: 'center', ...fadeInUpSX() }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: colors.primary.white }}>
              {searchTerm ? 'Nada encontrado' : 'Nenhum item neste nível'}
            </Typography>
            {searchTerm && (
              <Typography sx={{ marginTop: 0.5, fontSize: '12.5px', color: colors.brand.textSecondary }}>
                Nenhum resultado para "{searchTerm}" em {depth === 0 ? 'organizações' : pathStack[depth - 1]}.
              </Typography>
            )}
          </Box>
        ) : (
          <Box key={pathStack.join('/')} sx={staggerChildrenSX('button', 14, 15)}>
            {filteredItems.map(([key, value], index) => {
              const isFlow = !value;
              const childCount = countChildren(value);
              const eventInfo = eventHandlers?.[
                `${import.meta.env.VITE_AUTOMATIONS_BASE_URL}/webhooks/${[...pathStack, key].join('/')}`
              ] as TEventInfo[] | undefined;
              const isFirstResult = index === 0 && Boolean(searchTerm);
              const isOpenFlow = isFlow && selectedFlowPath === `/${[...pathStack, key].join('/')}`;

              return (
                <Box
                  key={key}
                  component="button"
                  type="button"
                  role="option"
                  aria-selected={isFirstResult || isOpenFlow}
                  aria-current={isOpenFlow ? 'true' : undefined}
                  onClick={() => openItem(key, value)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    width: '100%',
                    height: 42,
                    paddingX: 1,
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    backgroundColor: isOpenFlow
                      ? 'rgba(255, 160, 21, 0.14)'
                      : isFirstResult
                        ? 'rgba(255, 160, 21, 0.08)'
                        : 'transparent',
                    boxShadow: isOpenFlow ? `inset 2px 0 0 ${colors.primary.yellow}` : 'none',
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.04)' },
                    '&:hover .automation-chevron': { color: colors.primary.yellow, transform: 'translateX(2px)' },
                    '&:focus-visible': { outline: `2px solid ${colors.primary.yellow}`, outlineOffset: '-2px' },
                  }}
                >
                  {isFlow ? (
                    <BoltIcon sx={{ fontSize: 18, color: colors.primary.yellow, flexShrink: 0 }} />
                  ) : (
                    <FolderOutlinedIcon sx={{ fontSize: 18, color: colors.brand.textMuted, flexShrink: 0 }} />
                  )}
                  <Typography
                    noWrap
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      fontFamily: isFlow ? monoFontFamily : titleFontFamily,
                      fontSize: isFlow ? '13px' : '14px',
                      fontWeight: isFlow ? 500 : 400,
                      color: colors.primary.white,
                    }}
                  >
                    {key}
                  </Typography>

                  {isFlow && hasEventsLoaded && (
                    <Tooltip
                      title={
                        eventInfo?.length ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {eventInfo.map((event) => (
                              <Typography
                                key={`${event.source}-${event.detailType}`}
                                sx={{ fontFamily: monoFontFamily, fontSize: '11px' }}
                              >
                                {event.source} · {event.detailType}
                              </Typography>
                            ))}
                          </Box>
                        ) : (
                          'Nenhum event handler aponta para este fluxo'
                        )
                      }
                      placement="left"
                      arrow
                    >
                      <Box sx={tonePillSX(eventInfo?.length ? colors.status.concluded : colors.status.rejected)}>
                        {eventInfo?.length
                          ? `${eventInfo.length} evento${eventInfo.length > 1 ? 's' : ''}`
                          : 'sem evento'}
                      </Box>
                    </Tooltip>
                  )}

                  {!isFlow && (
                    <Typography sx={{ fontFamily: monoFontFamily, fontSize: '10.5px', color: colors.brand.textMuted }}>
                      {childCount}
                    </Typography>
                  )}
                  <ChevronRightIcon
                    className="automation-chevron"
                    sx={{
                      fontSize: 18,
                      flexShrink: 0,
                      color: colors.brand.textMuted,
                      transition: 'transform 0.15s ease, color 0.15s ease',
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          paddingX: { xs: 2, md: '22px' },
          height: 40,
          flexShrink: 0,
          borderTop: `1px solid ${colors.brand.line}`,
        }}
      >
        <Typography sx={{ ...monoLabelSX, flexGrow: 1 }}>
          {isLoading
            ? 'Carregando…'
            : `${filteredItems.length} ${filteredItems.length === 1 ? levelNoun : levelNounPlural}`}
        </Typography>
        {!isMobile && !isSplit && (
          <Typography sx={{ ...monoLabelSX, letterSpacing: '0.08em', textTransform: 'none' }}>
            ↵ abrir · Esc limpar · ⌫ voltar
          </Typography>
        )}
      </Box>
    </Paper>
  );

  const flowPanel = renderedFlowPath && (
    <FlowDiagramPanel
      key={renderedFlowPath}
      path={renderedFlowPath}
      dataSource={dataSource as TSources}
      port={port}
      onClose={onCloseFlow}
    />
  );

  if (isMobile) {
    return selectedFlowPath ? (
      <Box sx={{ height: 'calc(100vh - 92px)' }}>{flowPanel}</Box>
    ) : (
      <Box sx={{ display: 'flex', justifyContent: 'center', paddingY: 1 }}>{explorer}</Box>
    );
  }

  const transition = `${LAYOUT_DURATION_MS}ms ${LAYOUT_EASE}`;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        height: 'calc(100vh - 32px)',
        gap: isSplit ? 2 : 0,
        transition: `gap ${transition}`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexShrink: 0,
          maxWidth: '100%',
          width: isSplit ? SPLIT_EXPLORER_WIDTH : MODAL_WIDTH,
          marginLeft: isSplit ? '0px' : `calc((100% - ${MODAL_WIDTH}px) / 2)`,
          height: isSplit ? '100%' : 'min(680px, calc(100vh - 96px))',
          transition: `width ${transition}, margin-left ${transition}, height ${transition}`,
        }}
      >
        {explorer}
      </Box>
      <Box
        aria-hidden={!isSplit}
        sx={{
          flex: 1,
          minWidth: 0,
          height: '100%',
          opacity: isSplit ? 1 : 0,
          transform: isSplit ? 'translateX(0)' : 'translateX(32px)',
          pointerEvents: isSplit ? 'auto' : 'none',
          transition: `opacity 320ms ease ${isSplit ? '120ms' : '0ms'}, transform ${transition}`,
        }}
      >
        {flowPanel}
      </Box>
    </Box>
  );
};
